<template>
  <Flex w-full>
    <AmountField
      v-if="showInput"
      class="w-2/3"
      :class="item.id"
      :disabled="isReadOnlyDialog"
      @keydown.enter.prevent
      :units="dropdownUnits"
      :rules="[() => isInvalidAgainstCommonRules() || isInputValueInvalid()]"
      ref="input"
    />
    <Flex display-value>
      {{ convertedDisplayValue }}
    </Flex>
  </Flex>
</template>

<script>
import AmountField from '@wizer/core/components/amount-field/AmountField'
import Flex from '@wizer/core/components/flex/Flex'
import { mapGetters, mapState } from 'vuex'
import _ from 'lodash'
import Cleave from 'cleave.js'
import NumberFormatMixin from '@/mixins/NumberFormatMixin'
import SustainabilityInputMixin from '@/mixins/SustainabilityInputMixin'

export default {
  name: 'ConvertibleDecimalInput',
  mixins: [NumberFormatMixin, SustainabilityInputMixin],
  components: {
    AmountField,
    Flex,
  },
  props: {
    isReadOnlyDialog: Boolean,
    item: Object,
    previouslySaved: Boolean,
    contextValidationMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      cleave: null,
    }
  },
  mounted() {
    if (this.sourceUnit) {
      const updatedValue = {
        amount: _.isNumber(this.item.inputValue)
          ? this.getLocalizedInputValue(this.item.inputValue)
          : '',
        unit: {
          label: this.sourceUnit.name,
          value: this.sourceUnit.id,
        },
      }
      this.formValues[this.item.id] = updatedValue
    }
    this.cleave = new Cleave(
      `.${this.item.id} .w-amount-field__input`,
      this.getCleaveOptions(this.$profile.locale, this.onValueChanged),
    )
    if (this.item.inputValue) {
      this.cleave.setRawValue(this.item.inputValue)
    }
  },
  methods: {
    updateValue(value) {
      this.showInput = false
      this.formValues[this.item.id].amount = value
      this.$refs.input.validate()
      this.$nextTick(() => {
        this.showInput = true
      })
    },
    inputRequiresInitialAdjustment() {
      return true
    },
    onValueChanged(maskedValue) {
      this.formValues[this.item.id].amount = maskedValue.target.rawValue
      this.$refs.input.validate()
    },
    /**
     * Validates the input value
     */
    isInputValueInvalid() {
      if (this.contextValidationMessage) {
        return this.contextValidationMessage
      }
      const globalizedInputValue = this.getGlobalizedInputValue(this.inputValue)

      if (!globalizedInputValue) {
        return this.$t('validation.invalidDecimal')
      }
      const isInputValueValid = globalizedInputValue || this.isFloat(globalizedInputValue)
      const isConvertedValueValid = this.convertedValue || this.isFloat(this.convertedValue)
      if (
        !(
          isInputValueValid &&
          isConvertedValueValid &&
          this.isPrecisionKeptAfterSaving(globalizedInputValue)
        )
      ) {
        return this.$t('validation.invalidDecimal')
      }
      return true
    },
  },
  computed: {
    ...mapGetters('SustainabilityData', ['getConversionUnits']),
    ...mapState({
      formValues: (state) => state.SustainabilityData.formValues,
    }),
    formValue() {
      return this.formValues[this.item.id]
    },
    units() {
      return this.getConversionUnits(this.item.convertedUnitId)
    },
    dropdownUnits() {
      return this.units.map((unit) => ({
        value: unit.id,
        label: unit.name,
      }))
    },
    sourceUnit() {
      if (this.formValue && this.formValue.unit && this.formValue.unit.value) {
        return this.units.find((unit) => unit.id === this.formValue.unit.value)
      }
      return this.units.find((unit) => unit.id === this.item.inputUnitId)
    },
    targetUnit() {
      return this.units.find((unit) => unit.id === this.item.convertedUnitId)
    },
    inputValue() {
      return this.formValue ? this.formValue.amount : undefined
    },
    convertedValue() {
      const globalizedInputValue = this.getGlobalizedInputValue(this.inputValue)
      if (!globalizedInputValue && globalizedInputValue !== 0) {
        return null
      }
      return globalizedInputValue * this.sourceUnit.factor
    },
    convertedDisplayValue() {
      if ((this.convertedValue || this.convertedValue === 0) && this.targetUnit) {
        const formattedValue = this.formatForConvertedSustainabilityMetric(
          this.convertedValue,
          this.$profile.locale,
        )
        return `${formattedValue} ${this.targetUnit.name}`
      }
      // Display target UoM in case no value is provided
      if (this.targetUnit) {
        return `${this.targetUnit.name}`
      }
      return ''
    },
    inputValueToCompare() {
      return this.convertedValue
    },
    inputValueToPost() {
      return {
        inputValue: this.getGlobalizedInputValue(this.inputValue),
        inputUnitItem: this.sourceUnit.id,
        convertedUnit: this.targetUnit.id,
        convertedValue: this.convertedValue,
        conversionId: this.sourceUnit.conversionId,
        adjusted: true,
      }
    },
  },
}
</script>

<style scoped lang="postcss">
.display-value {
  @apply w-1/3 px-3 py-2 ml-3 bg-gray-200 border rounded-sm border-gray-300;
}
</style>
